// Ops-side mirror of the crew app's Availability screen (Task 6): same two
// shapes sharing one table (Task 5's /admin/vendors/:id/availability-
// exclusions) — a whole-crew "offline" row (product AND categoryId both
// null, covering today) called out at the top, and everything else — a
// product/category/date-range row — listed with an add form below it. Bid
// eligibility and search already honour every row via
// exclusionNotExistsSql; this section only manages them, on the crew's
// behalf, from the vendor detail page.
//
// Own queries and own save, same shape as VendorTransfersSection /
// VendorSheetSection: not folded into VendorDetail's draft/buildPatch flow,
// because this table has its own audit entries and its own endpoint.
import { TRANSFERS_UI_ENABLED } from '@/lib/features';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailSection, ErrorNotice } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { ApiError } from '@/lib/api';
import {
  createVendorExclusion,
  deleteVendorExclusion,
  fetchVendorAvailability,
  type VendorAvailabilityExclusion,
} from '@/lib/admin-api';

type Product = 'rentals' | 'transfers' | null;

/** A row is the whole-crew offline switch iff it excludes every product and
 *  every category — not a specific product with no category chosen. */
function isOffline(row: VendorAvailabilityExclusion): boolean {
  return row.product === null && row.categoryId === null;
}

function productLabel(product: Product): string {
  if (product === 'rentals') return 'Car rentals';
  if (product === 'transfers') return 'Airport transfers';
  return 'Everything';
}

/** The qualifier shown after the product name when no specific category was
 *  chosen — "All types" for rentals, "All classes" for transfers. A
 *  whole-crew (no product) row never reaches here: it has its own "Offline
 *  until…" label and no qualifier. */
function categoryQualifier(row: VendorAvailabilityExclusion): string | null {
  if (row.categoryName) return row.categoryName;
  if (row.product === 'rentals') return 'All types';
  if (row.product === 'transfers') return 'All classes';
  return null;
}

/** "Added by ops (email)" / "Added by crew (email)" — email may be null, in
 *  which case the parenthesis is omitted rather than shown empty.
 *
 *  A `vendor` actor is a vendors-audience login: the COMPANY itself rather
 *  than a person on the crew. It is still the crew's own doing, so it reads
 *  as crew — qualified "(company login)" so ops can tell which of the two
 *  crew-side doors wrote the row when the email alone would not say. */
function whoLabel(createdBy: VendorAvailabilityExclusion['createdBy']): string {
  const who =
    createdBy.kind === 'admin' ? 'ops' : createdBy.kind === 'vendor' ? 'crew (company login)' : 'crew';
  return createdBy.email ? `Added by ${who} (${createdBy.email})` : `Added by ${who}`;
}

const ADD_ERROR_CODES = ['date_order', 'category_requires_product', 'category_not_sold', 'product_not_sold'] as const;

function mappedError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | undefined;
    if (body?.error && (ADD_ERROR_CODES as readonly string[]).includes(body.error) && body.message) {
      return body.message;
    }
  }
  return 'Could not add that exclusion.';
}

export function VendorAvailabilitySection({ vendorId }: { vendorId: number }) {
  const qc = useQueryClient();
  const key = ['admin', 'vendors', vendorId, 'availability-exclusions'];

  const q = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchVendorAvailability(vendorId, signal),
  });

  const [addError, setAddError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [goingOffline, setGoingOffline] = useState(false);
  const [offlineUntil, setOfflineUntil] = useState('');
  const [product, setProduct] = useState<Product>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [note, setNote] = useState('');

  // A crew that stops selling transfers (products save on the sibling
  // section, refetched independently) can leave "transfers" selected in this
  // form with a category that no longer means anything — the select would
  // still offer classes for a product this crew can't be excluded from
  // because it doesn't sell it. Drop back to "Everything" instead of leaving
  // a stale, about-to-fail choice selected.
  useEffect(() => {
    if (product === 'transfers' && q.data && !q.data.pickLists.sellsTransfers) {
      setProduct(null);
      setCategoryId(null);
    }
  }, [q.data, product]);

  const create = useMutation({
    mutationFn: (body: { product: Product; categoryId: number | null; dateFrom: string; dateTo: string; note: string | null }) =>
      createVendorExclusion(vendorId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const remove = useMutation({
    mutationFn: (exclusionId: number) => deleteVendorExclusion(vendorId, exclusionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const resetAddForm = () => {
    setProduct(null);
    setCategoryId(null);
    setDateFrom('');
    setDateTo('');
    setNote('');
  };

  const submitAdd = () => {
    setAddError(null);
    // String comparison is safe here: both are the API's YYYY-MM-DD shape,
    // which sorts lexically the same as chronologically. Checked client-side
    // so a reversed range never reaches the network — the server would
    // refuse it anyway (date_order), but there is no reason to round-trip
    // for a check the form can make instantly.
    if (dateTo < dateFrom) {
      setAddError('The end date is before the start date.');
      return;
    }
    create.mutate(
      { product, categoryId, dateFrom, dateTo, note: note.trim() === '' ? null : note.trim() },
      { onSuccess: () => resetAddForm(), onError: (err) => setAddError(mappedError(err)) },
    );
  };

  const goOffline = () => {
    setAddError(null);
    // The offline row runs from `today`, so an "until" earlier than today is
    // the same reversed range the add form refuses — same copy, refused the
    // same way, before the network. The input also carries `min`, so a picker
    // will not offer such a date in the first place.
    if (q.data && offlineUntil < q.data.today) {
      setAddError('The end date is before the start date.');
      return;
    }
    create.mutate(
      { product: null, categoryId: null, dateFrom: q.data?.today ?? '', dateTo: offlineUntil, note: null },
      {
        onSuccess: () => {
          setGoingOffline(false);
          setOfflineUntil('');
        },
        onError: (err) => setAddError(mappedError(err)),
      },
    );
  };

  const doRemove = (id: number) => {
    setRemoveError(null);
    remove.mutate(id, { onError: () => setRemoveError('Could not remove that exclusion.') });
  };

  const categoryOptions = useMemo(() => {
    if (!q.data) return [];
    if (product === 'rentals') return q.data.pickLists.vehicleTypes;
    if (product === 'transfers') return q.data.pickLists.transferClasses;
    return [];
  }, [q.data, product]);

  return (
    <DetailSection title="Availability exclusions">
      {q.isLoading ? <p className="text-[12.5px] text-[color:var(--bo-mute)]">Loading…</p> : null}
      {q.isError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
          Couldn&apos;t load availability exclusions.
        </div>
      ) : null}

      {q.data ? (
        <>
          {(() => {
            const { exclusions, today } = q.data;
            // Two whole-crew rows can overlap today (e.g. an extension added
            // before the first one lapsed) — the one that keeps the crew
            // offline LONGEST is the one that governs, so pick the latest
            // dateTo rather than the first match.
            const offlineRow = exclusions
              .filter((row) => isOffline(row) && row.dateFrom <= today && row.dateTo >= today)
              .reduce<VendorAvailabilityExclusion | null>(
                (latest, row) => (latest == null || row.dateTo > latest.dateTo ? row : latest),
                null,
              );
            const scopedRows = exclusions.filter((row) => row.id !== offlineRow?.id);

            return (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-[color:var(--bo-line)] pb-4">
                  <p className="text-[13px]">
                    {offlineRow ? `Offline until ${offlineRow.dateTo}` : 'Online now.'}
                  </p>
                  {offlineRow ? (
                    <RowAction busy={remove.isPending} onClick={() => doRemove(offlineRow.id)}>
                      Back online
                    </RowAction>
                  ) : goingOffline ? (
                    <span className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-[12px]">
                        Until
                        <input
                          type="date"
                          aria-label="Until"
                          min={today}
                          value={offlineUntil}
                          onChange={(e) => setOfflineUntil(e.target.value)}
                          className="rounded-md border border-[color:var(--bo-line)] px-1.5 py-1"
                        />
                      </label>
                      <RowAction
                        kind="primary"
                        busy={create.isPending}
                        disabled={offlineUntil === ''}
                        onClick={goOffline}
                      >
                        Go offline until…
                      </RowAction>
                    </span>
                  ) : (
                    <RowAction onClick={() => setGoingOffline(true)}>Go offline until…</RowAction>
                  )}
                </div>

                {removeError ? (
                  <p role="alert" className="mb-3 rounded-lg bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
                    {removeError}
                  </p>
                ) : null}

                {scopedRows.length === 0 ? (
                  <p className="text-[12.5px] text-[color:var(--bo-mute)]">
                    No exclusions. Everything this crew lists is available.
                  </p>
                ) : (
                  <ul className="mb-4 divide-y divide-[color:var(--bo-line)] border-b border-[color:var(--bo-line)]">
                    {scopedRows.map((row) => (
                      <li key={row.id} className="flex flex-wrap items-center gap-3 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium">
                            {productLabel(row.product)}
                            {categoryQualifier(row) ? ` · ${categoryQualifier(row)}` : ''}
                          </div>
                          <div className="text-[12px] text-[color:var(--bo-mute)]">
                            {row.dateFrom} – {row.dateTo} · {whoLabel(row.createdBy)}
                          </div>
                          {row.note ? (
                            <div className="mt-1 text-[12px] text-[color:var(--bo-mute)]">{row.note}</div>
                          ) : null}
                        </div>
                        <RowAction busy={remove.isPending} onClick={() => doRemove(row.id)}>
                          Remove
                        </RowAction>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            );
          })()}

          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-[12px]">
              Product
              <select
                aria-label="Product"
                value={product ?? ''}
                onChange={(e) => {
                  const next = e.target.value === '' ? null : (e.target.value as 'rentals' | 'transfers');
                  setProduct(next);
                  setCategoryId(null);
                }}
                className="rounded-md border border-[color:var(--bo-line)] px-2 py-1.5 text-[12.5px]"
              >
                <option value="">Everything</option>
                <option value="rentals">Car rentals</option>
                {TRANSFERS_UI_ENABLED && q.data?.pickLists.sellsTransfers ? (
                  <option value="transfers">Airport transfers</option>
                ) : null}
              </select>
            </label>

            <label className="grid gap-1 text-[12px]">
              Type or class
              <select
                aria-label="Type or class"
                value={categoryId ?? ''}
                onChange={(e) => setCategoryId(e.target.value === '' ? null : Number(e.target.value))}
                disabled={product === null}
                className="rounded-md border border-[color:var(--bo-line)] px-2 py-1.5 text-[12.5px] disabled:opacity-50"
              >
                <option value="">All</option>
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="grid gap-1 text-[12px]">
              From
              <input
                type="date"
                aria-label="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-md border border-[color:var(--bo-line)] px-2 py-1.5 text-[12.5px]"
              />
            </label>

            <label className="grid gap-1 text-[12px]">
              To
              <input
                type="date"
                aria-label="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-md border border-[color:var(--bo-line)] px-2 py-1.5 text-[12.5px]"
              />
            </label>

            <label className="grid min-w-[12rem] flex-1 gap-1 text-[12px]">
              Note
              <input
                type="text"
                aria-label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="rounded-md border border-[color:var(--bo-line)] px-2 py-1.5 text-[12.5px]"
              />
            </label>

            <RowAction
              kind="primary"
              busy={create.isPending}
              disabled={dateFrom === '' || dateTo === ''}
              onClick={submitAdd}
            >
              Add exclusion
            </RowAction>
          </div>

          {addError ? (
            <div className="mt-3">
              <ErrorNotice message={addError} />
            </div>
          ) : null}
        </>
      ) : null}
    </DetailSection>
  );
}
